import { z } from 'zod';

/**
 * The getting-started demo seed bundle: a frozen export of the golden demo
 * space plus the two recorded generation replays. Produced by
 * backend-service/scripts/freeze-demo-bundle.ts; consumed by the per-user
 * seeder (lib/demo-space.ts) and the demo-replay engine
 * (api/ai/demo-replay/).
 *
 * Every id in the bundle is a symbolic `{{key}}` placeholder, never a real
 * database id. The seeder/engine substitute fresh ids per user; the freeze
 * script fails closed if any real id pattern survives export.
 */

// Symbolic placeholder, e.g. "{{deck}}", "{{slide-07}}", "{{sheet-dcf}}".
export const DemoSymbolSchema = z
  .string()
  .regex(/^\{\{[a-z0-9-]+\}\}$/, 'demo bundle ids must be {{symbolic}}');

export const DemoBundleFolderSchema = z.object({
  key: z.string(),
  name: z.string(),
  parentKey: z.string().nullable(),
  indexNumber: z.string(),
});

export const DemoBundleFileSchema = z.object({
  key: z.string(),
  name: z.string(),
  folderKey: z.string().nullable(),
  fileType: z.string(),
  mimeType: z.string(),
  size: z.number().int().nonnegative(),
  pageCount: z.number().int().positive().nullable(),
  indexNumber: z.string(),
  /** Shared immutable blob under demo-bundle/v{N}/files/ — one copy for all users. */
  s3Key: z.string(),
  extractedMarkdown: z.string().nullable(),
  /** Number of chunk rows for this file in the chunks sidecar. */
  chunkCount: z.number().int().nonnegative(),
});

// One row of the chunks.ndjson sidecar (embeddings are too large to inline in
// the manifest). `embedding` is the pgvector literal string, e.g. "[0.1,...]".
export const DemoBundleChunkSchema = z.object({
  fileKey: z.string(),
  chunkIndex: z.number().int().nonnegative(),
  content: z.string(),
  embedding: z.string().nullable(),
  metadata: z.unknown().nullable(),
});

// A Crow Capital Markets template deck seeded as a finished native deck in
// the demo space's Templates folder. Ids inside slide HTML (linked-chart
// dataSource refs) are {{symbolic}}; the seeder mints fresh ids per user and
// substitutes them. Slide HTML embeds all images as data: URIs — no external
// asset dependencies.
export const DemoBundleTemplateDeckSchema = z.object({
  key: z.string(),
  name: z.string().min(1),
  format: z.enum(['widescreen', 'standard']),
  folderKey: z.string().nullable(),
  outline: z.string().nullable(),
  /** Symbol standing for the deck's own id inside slide HTML/outline. */
  deckSymbol: DemoSymbolSchema,
  slides: z
    .array(
      z.object({
        order: z.number().int().nonnegative(),
        html: z.string().min(1),
        notes: z.string().nullable(),
      })
    )
    .min(1),
  /** Chart backing workbook, cloned per user with ownerDocumentId = the new deck. */
  chartWorkbook: z
    .object({
      symbol: DemoSymbolSchema,
      name: z.string().min(1),
      sheets: z
        .array(
          z.object({
            symbol: DemoSymbolSchema,
            order: z.number().int().nonnegative(),
            name: z.string().min(1),
            data: z.unknown(),
          })
        )
        .min(1),
    })
    .nullable(),
});

export const DemoReplayStepStatusSchema = z.enum([
  'pending',
  'in_progress',
  'completed',
  'failed',
]);

// Timeline items fire at `at` ms offsets from replay start (already re-paced
// by the freeze script). Command envelopes and agent events carry {{symbolic}}
// ids; sequences are NEVER stored — the op-log assigns them at apply time.
export const DemoReplayTimelineItemSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('agent.event'),
    at: z.number().int().nonnegative(),
    /** PcAgentStreamEvent payload with symbolic ids. Deltas are broadcast-only. */
    event: z.record(z.string(), z.unknown()),
  }),
  // Plants deal-team feedback threads on a built slide (the tour's comments
  // beat). Author is the demo-space owner with a persona display name.
  z.object({
    kind: z.literal('comments.plant'),
    at: z.number().int().nonnegative(),
    threads: z
      .array(
        z.object({
          threadSymbol: DemoSymbolSchema,
          /** Symbolic slide id the thread anchors to. */
          slideSymbol: DemoSymbolSchema,
          /** DOM element id inside the slide html the pin attaches to. */
          elementId: z.string().nullable(),
          x: z.number().nullable(),
          y: z.number().nullable(),
          authorDisplayName: z.string().min(1),
          body: z.string().min(1),
        })
      )
      .min(1),
  }),
  z.object({
    kind: z.literal('comments.resolve'),
    at: z.number().int().nonnegative(),
    threadSymbols: z.array(DemoSymbolSchema).min(1),
  }),
  z.object({
    kind: z.literal('deck.commands'),
    at: z.number().int().nonnegative(),
    /** Deck command envelopes (generated-deck-command-service input shape). */
    envelopes: z.array(z.record(z.string(), z.unknown())).min(1),
  }),
  z.object({
    kind: z.literal('workbook.commands'),
    at: z.number().int().nonnegative(),
    /** Workbook command envelopes (workbook-command-service input shape). */
    envelopes: z.array(z.record(z.string(), z.unknown())).min(1),
  }),
  z.object({
    kind: z.literal('step'),
    at: z.number().int().nonnegative(),
    step: z.object({
      activityKey: z.string(),
      kind: z.string(),
      title: z.string(),
      detail: z.string().nullable().optional(),
      slide: z.number().int().nullable().optional(),
      status: DemoReplayStepStatusSchema,
    }),
  }),
]);

export const DemoReplayScriptSchema = z.object({
  id: z.enum(['sheet', 'deck', 'comments']),
  /** The recorded user prompt, pre-filled by the tour and shown in chat. */
  prompt: z.string().min(1),
  /**
   * Every symbolic key the script references, mapped to what it stands for.
   * The engine mints one fresh id per key per user and records the mapping on
   * UserDemoSpace.idMap so the deck replay can reference the sheet replay's
   * workbook.
   */
  idMap: z.record(
    z.string(),
    z.enum([
      'deck',
      'workbook',
      'chartWorkbook',
      'slide',
      'sheet',
      'session',
      'commentThread',
    ])
  ),
  timeline: z.array(DemoReplayTimelineItemSchema).min(1),
  /** Final assistant chat message persisted at completion. */
  assistantMessage: z.string().min(1),
  /**
   * Raw LLM-history rows (AgentMessage.parts payloads) persisted at
   * completion so live follow-up runs see coherent context.
   */
  agentMessages: z.array(
    z.object({ role: z.enum(['user', 'assistant']), parts: z.unknown() })
  ),
  /** Document title the UI/creation flow should use, e.g. "Project Alpine — Valuation Model". */
  documentName: z.string().min(1),
});

export const DemoBundleManifestSchema = z.object({
  version: z.number().int().positive(),
  createdAt: z.string(),
  sourceSpaceId: z.string(),
  spaceName: z.string().min(1),
  folders: z.array(DemoBundleFolderSchema),
  files: z.array(DemoBundleFileSchema),
  /** Default keeps pre-template manifests (local fixtures) parseable. */
  templateDecks: z.array(DemoBundleTemplateDeckSchema).default([]),
  /** Relative path of the chunks sidecar within the bundle prefix. */
  chunksFile: z.string(),
  replays: z.object({
    sheet: DemoReplayScriptSchema,
    deck: DemoReplayScriptSchema,
    // Required: the tour's stage machine always reaches the comments beat, so
    // a bundle without this replay would strand every user at that step.
    comments: DemoReplayScriptSchema,
  }),
});

export type DemoBundleFolder = z.infer<typeof DemoBundleFolderSchema>;
export type DemoBundleFile = z.infer<typeof DemoBundleFileSchema>;
export type DemoBundleTemplateDeck = z.infer<typeof DemoBundleTemplateDeckSchema>;
export type DemoBundleChunk = z.infer<typeof DemoBundleChunkSchema>;
export type DemoReplayTimelineItem = z.infer<typeof DemoReplayTimelineItemSchema>;
export type DemoReplayScript = z.infer<typeof DemoReplayScriptSchema>;
export type DemoBundleManifest = z.infer<typeof DemoBundleManifestSchema>;

export const DEMO_BUNDLE_PREFIX = 'demo-bundle';

export function demoBundlePrefix(version: number): string {
  return `${DEMO_BUNDLE_PREFIX}/v${version}`;
}

/** Guard for the shared immutable bundle blobs: never S3-delete these keys. */
export function isDemoBundleKey(s3Key: string): boolean {
  return s3Key.startsWith(`${DEMO_BUNDLE_PREFIX}/`);
}
